import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Shield, BellRing, CreditCard, FileText, Settings, LogOut, Menu, X, Users, UserCog, ChevronDown, ChevronRight, Mail, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import logo from '../assets/logo-asistelo.png';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menús condicionales según el rol
  const getNavigation = () => {
    if (user?.role === 'super_admin') {
      // Super Admin solo ve Usuarios Principales
      return [
        { name: 'Usuarios Principales', href: '/principal-users', icon: UserCog, testId: 'nav-principal-users' },
      ];
    } else if (user?.role === 'admin') {
      // Admin (Usuario Principal) ve todo excepto gestión de Usuarios Principales
      return [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard, testId: 'nav-dashboard' },
        { name: 'Pipeline', href: '/pipeline', icon: TrendingUp, testId: 'nav-pipeline' },
        { name: 'Clientes', href: '/clients', icon: Users, testId: 'nav-clients' },
        { name: 'Pólizas', href: '/policies', icon: Shield, testId: 'nav-policies' },
        { name: 'Pagos', href: '/payments', icon: CreditCard, testId: 'nav-payments' },
        { name: 'Recordatorios', href: '/reminders', icon: BellRing, testId: 'nav-reminders' },
        { name: 'Bandeja de Salida', href: '/email-outbox', icon: Mail, testId: 'nav-outbox' },
        { 
          name: 'Configuración', 
          icon: Settings, 
          testId: 'nav-config',
          isSubmenu: true,
          submenu: [
            { name: 'Comisiones', href: '/commission-config', testId: 'nav-commissions' },
            { name: 'Planes', href: '/plans', testId: 'nav-plans' },
            { name: 'Catálogos', href: '/catalogs', testId: 'nav-catalogs' },
          ]
        },
        { name: 'Usuarios Asistentes', href: '/users', icon: UserCog, testId: 'nav-users' },
      ];
    } else {
      // Assistant solo ve operaciones básicas
      return [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard, testId: 'nav-dashboard' },
        { name: 'Pólizas', href: '/policies', icon: Shield, testId: 'nav-policies' },
        { name: 'Pagos', href: '/payments', icon: CreditCard, testId: 'nav-payments' },
        { name: 'Recordatorios', href: '/reminders', icon: BellRing, testId: 'nav-reminders' },
      ];
    }
  };

  const navigation = getNavigation();

  // Check if any submenu item is active
  const isSubmenuActive = (submenu) => {
    return submenu?.some(item => location.pathname === item.href);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#e0e0e0] p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Asistelo" className="h-6 w-auto" />
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-50 rounded-[10px]"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed top-0 left-0 z-40 w-64 h-screen transition-transform bg-white border-r border-[#e0e0e0] lg:top-0 mt-16 lg:mt-0`}
        data-testid="sidebar"
      >
        <div className="h-full px-3 py-4 overflow-y-auto flex flex-col">
          <div className="mb-8 px-3 hidden lg:block">
            <div className="flex items-center gap-3 mb-2">
              <img src={logo} alt="Asistelo" className="h-9 w-auto" />
            </div>
          </div>

          <nav className="space-y-1 flex-1">
            {navigation.map((item) => {
              if (item.isSubmenu) {
                const isActive = isSubmenuActive(item.submenu);
                const Icon = item.icon;
                return (
                  <div key={item.name}>
                    <button
                      data-testid={item.testId}
                      onClick={() => setConfigMenuOpen(!configMenuOpen)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-[10px] transition-colors ${
                        isActive
                          ? 'bg-[#2537b0] text-white'
                          : 'text-slate-700 hover:bg-blue-50 hover:text-[#2537b0]'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon size={20} className="mr-3" strokeWidth={1.5} />
                        {item.name}
                      </div>
                      {configMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {configMenuOpen && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const isSubActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              data-testid={subItem.testId}
                              className={`flex items-center px-3 py-2 text-sm font-medium rounded-[10px] transition-colors ${
                                isSubActive
                                  ? 'bg-blue-100 text-[#2537b0]'
                                  : 'text-slate-600 hover:bg-blue-50 hover:text-[#2537b0]'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              } else {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={item.testId}
                    className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-[10px] transition-colors ${
                      isActive
                        ? 'bg-[#2537b0] text-white'
                        : 'text-slate-700 hover:bg-blue-50 hover:text-[#2537b0]'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={20} className="mr-3" strokeWidth={1.5} />
                    {item.name}
                  </Link>
                );
              }
            })}
            
            {/* Config. Resend (Sistema) - Solo para Super Admin */}
            {user?.role === 'super_admin' && (
              <Link
                to="/system/resend-config"
                data-testid="nav-resend-config"
                className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-[10px] transition-colors ${
                  location.pathname === '/system/resend-config'
                    ? 'bg-[#2537b0] text-white'
                    : 'text-slate-700 hover:bg-blue-50 hover:text-[#2537b0]'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Mail size={20} className="mr-3" strokeWidth={1.5} />
                Config. Resend (Sistema)
              </Link>
            )}
          </nav>

          <div className="border-t border-[#e0e0e0] pt-4 mt-4">
            <div className="px-3 mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Usuario
              </p>
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
              <p className="text-xs text-slate-400 mt-1">
                Rol: {user?.role === 'super_admin' ? 'Super Administrador' : user?.role === 'admin' ? 'Usuario Principal' : 'Asistente'}
              </p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-[#2537b0] hover:bg-blue-50"
            >
              <LogOut size={20} className="mr-3" strokeWidth={1.5} />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 mt-16 lg:mt-0">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
